import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format, parseISO, isValid } from 'date-fns';
import DataTablePagination from '@/components/data-table/DataTablePagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import InventoryTimeSeriesGraph from './InventoryTimeSeriesGraph';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InventoryTimeSeriesViewProps {
  data: any[];
  fullData: any[]; // All data for graph view
  isLoading: boolean;
  error: any;
  totalCount: number;
  currentPage: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (size: number) => void;
  onViewModeChange?: (mode: 'table' | 'graph') => void;
  selectedSkus?: string[];
}

const InventoryTimeSeriesView: React.FC<InventoryTimeSeriesViewProps> = ({
  data,
  fullData, // All data for graph view
  isLoading,
  error,
  totalCount,
  currentPage,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onViewModeChange,
  selectedSkus = []
}) => {
  const [viewMode, setViewMode] = useState<'table' | 'graph'>('table');
  const [expandedSkus, setExpandedSkus] = useState<Set<string>>(new Set());
  const [expandedDateRows, setExpandedDateRows] = useState<Set<string>>(new Set());

  // Notify parent when view mode changes
  useEffect(() => {
    onViewModeChange?.(viewMode);
  }, [viewMode, onViewModeChange]);

  // Reset expanded rows when data changes
  useEffect(() => {
    setExpandedSkus(new Set());
    setExpandedDateRows(new Set());
  }, [data]);

  const toggleSkuExpansion = (sku: string) => {
    const newExpanded = new Set(expandedSkus);
    if (newExpanded.has(sku)) {
      newExpanded.delete(sku);
    } else {
      newExpanded.add(sku);
    }
    setExpandedSkus(newExpanded);
  };

  const toggleDateRowExpansion = (key: string) => {
    const newExpanded = new Set(expandedDateRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedDateRows(newExpanded);
  };

  const hasMultipleSources = (row: any): boolean => {
    if (row.sourceBreakdown && typeof row.sourceBreakdown === 'object') {
      return Object.keys(row.sourceBreakdown).length > 1;
    }
    // Fallback: comma-separated sources
    const source = row.source || '';
    return String(source).includes(',');
  };

  // Group data by SKU
  const groupedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const groups = new Map<string, any>();

    data.forEach((row: any) => {
      const sku = row.sku || row['SKU (Unique Identifier)'];
      if (!sku) return;

      if (!groups.has(sku)) {
        groups.set(sku, {
          sku,
          sources: new Set<string>(),
          entries: []
        });
      }

      const group = groups.get(sku);
      const quantity = Number(row.quantity ?? row.Held ?? 0);
      const source = row.source || row.Location;

      // Add sources from sourceBreakdown if available
      if (row.sourceBreakdown && typeof row.sourceBreakdown === 'object') {
        Object.keys(row.sourceBreakdown).forEach(s => group.sources.add(s));
      } else if (source) {
        String(source).split(',').map(s => s.trim()).forEach(s => group.sources.add(s));
      }

      group.entries.push({
        date: row.date,
        quantity,
        source,
        sourceBreakdown: row.sourceBreakdown
      });
    });

    // Convert to array and sort entries by date
    return Array.from(groups.values()).map(group => ({
      ...group,
      sources: Array.from(group.sources).sort().join(', '),
      entries: group.entries.sort((a: any, b: any) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB.getTime() - dateA.getTime(); // Most recent first
      })
    }));
  }, [data]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  if (error) {
    return (
      <div className="border border-destructive/20 text-destructive rounded-md p-6 text-center">
        Failed to load time-series: {String(error)}
      </div>
    );
  }

  // Only show full loading state if we have no data at all
  // If we have fullData (existing graph), keep showing it with a loading indicator
  if (isLoading && (!fullData || fullData.length === 0)) {
    return (
      <div className="border rounded-md p-6 text-center text-muted-foreground">
        Loading trends...
      </div>
    );
  }

  // Check the appropriate data based on view mode
  const dataToCheck = viewMode === 'graph' ? fullData : data;
  if (!dataToCheck || dataToCheck.length === 0) {
    return (
      <div className="border rounded-md p-6 text-center text-muted-foreground">
        No time-series data for the selected range.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium">Inventory Trends</CardTitle>
            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'table' | 'graph')}>
              <TabsList>
                <TabsTrigger value="table">Table</TabsTrigger>
                <TabsTrigger value="graph">Graph</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'table' ? (
            <>
              <div className="overflow-auto max-h-[600px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Sources</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedData.map((group) => {
                      const isSkuExpanded = expandedSkus.has(group.sku);

                      return (
                        <React.Fragment key={group.sku}>
                          {/* Parent SKU Row */}
                          <TableRow
                            className="cursor-pointer hover:bg-muted/50 font-medium"
                            onClick={() => toggleSkuExpansion(group.sku)}
                          >
                            <TableCell className="w-8">
                              <div className="flex items-center justify-center">
                                {isSkuExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-semibold">{group.sku}</TableCell>
                            <TableCell className="text-sm">{group.sources}</TableCell>
                          </TableRow>

                          {/* Child Date Rows */}
                          {isSkuExpanded && group.entries.map((entry: any, entryIdx: number) => {
                            const dateRowKey = `${group.sku}-${entryIdx}`;
                            const isDateRowExpanded = expandedDateRows.has(dateRowKey);
                            const hasMultipleSources = entry.sourceBreakdown &&
                              typeof entry.sourceBreakdown === 'object' &&
                              Object.keys(entry.sourceBreakdown).length > 1;

                            let formattedDate = '-';
                            try {
                              const dateValue = entry.date;
                              if (dateValue) {
                                const dateObj = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
                                if (isValid(dateObj)) {
                                  formattedDate = format(dateObj, 'MMM d, yyyy');
                                }
                              }
                            } catch (e) {
                              formattedDate = String(entry.date || '-');
                            }

                            return (
                              <React.Fragment key={dateRowKey}>
                                <TableRow
                                  className={cn(
                                    "bg-muted/20",
                                    hasMultipleSources && "cursor-pointer hover:bg-muted/30"
                                  )}
                                  onClick={(e) => {
                                    if (hasMultipleSources) {
                                      e.stopPropagation();
                                      toggleDateRowExpansion(dateRowKey);
                                    }
                                  }}
                                >
                                  <TableCell className="w-8 pl-8">
                                    {hasMultipleSources && (
                                      <div className="flex items-center justify-center">
                                        {isDateRowExpanded ? (
                                          <ChevronDown className="h-3 w-3 text-muted-foreground" />
                                        ) : (
                                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                        )}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell className="pl-8">
                                    <div className="text-sm">
                                      <div className="font-medium">{formattedDate}</div>
                                      <div className="text-xs text-muted-foreground">Quantity: {entry.quantity.toLocaleString()}</div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {entry.source || '-'}
                                  </TableCell>
                                </TableRow>

                                {/* Source breakdown for individual date */}
                                {isDateRowExpanded && hasMultipleSources && (
                                  <TableRow className="bg-muted/10">
                                    <TableCell />
                                    <TableCell colSpan={2} className="py-3">
                                      <div className="pl-16">
                                        <div className="text-xs font-semibold mb-2 text-muted-foreground">
                                          Source Breakdown:
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 max-w-md">
                                          {Object.entries(entry.sourceBreakdown || {})
                                            .sort(([a], [b]) => a.localeCompare(b))
                                            .map(([source, quantity]) => {
                                              const numQuantity = typeof quantity === 'number'
                                                ? quantity
                                                : parseFloat(String(quantity || '0'));
                                              return (
                                                <div
                                                  key={source}
                                                  className="flex justify-between items-center p-2 bg-background rounded border text-xs"
                                                >
                                                  <span className="font-medium">{source}:</span>
                                                  <span className="text-muted-foreground">
                                                    {numQuantity.toLocaleString()} units
                                                  </span>
                                                </div>
                                              );
                                            })}
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4">
                <div className="text-sm text-center text-muted-foreground mb-2">
                  Showing page {currentPage} of {totalPages} ({groupedData.length} of {totalCount.toLocaleString()} entries)
                </div>
                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                />
              </div>
            </>
          ) : (
            <div className="py-4 relative">
              {isLoading && (
                <div className="absolute top-2 right-2 z-10 flex items-center gap-2 bg-background/80 backdrop-blur-sm border rounded-md px-3 py-1.5 text-sm text-muted-foreground shadow-sm">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading new SKU data...
                </div>
              )}
              <InventoryTimeSeriesGraph data={fullData} selectedSkus={selectedSkus} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryTimeSeriesView;
